import { __read, __spread } from "tslib";
import { useCallback, useEffect, useMemo, useRef, useState, createRef } from 'react';
import { useForwardedRef } from '@use-platform/react/libs/forward-ref';
import { useOverlay } from '@use-platform/react/semantic/overlay';
import { useUserRPC } from './useUserRPC';
import { useIframeMaxHeight, canUseDOM, getClientHeight } from './useIframeMaxHeight';
import { usePlatform } from './usePlatform';
export function useUserID(props) {
    var avatarRefRaw = props.avatarRef, _a = props.avatarSize, avatarSize = _a === void 0 ? [42, 22] : _a, _b = props.colorScheme, colorScheme = _b === void 0 ? 'light' : _b, dialogRefRaw = props.dialogRef, _c = props.dir, dir = _c === void 0 ? 'ltr' : _c, _d = props.env, env = _d === void 0 ? 'prod' : _d, features = props.features, fetchUserData = props.fetchUserData, host = props.host, iframeRefRaw = props.iframeRef, lang = props.lang, onCloseRaw = props.onClose, onMenuItemClick = props.onMenuItemClick, onOpen = props.onOpen, onThemeChange = props.onThemeChange, platformRaw = props.platform, _e = props.preload, preloadRaw = _e === void 0 ? false : _e, project = props.project, queryParams = props.queryParams, retpathRaw = props.retpath, rpcHandlers = props.rpcHandlers, theme = props.theme, _f = props.tld, tld = _f === void 0 ? 'ru' : _f, touchBreakpointWidth = props.touchBreakpointWidth, userData = props.userData, _g = props.visible, visible = _g === void 0 ? false : _g;
    var avatarRef = useRef(null);
    var dialogRef = useRef(null);
    var iframeRef = useRef(null);
    useForwardedRef(avatarRef, avatarRefRaw || createRef());
    dialogRef = dialogRefRaw !== null && dialogRefRaw !== void 0 ? dialogRefRaw : dialogRef;
    iframeRef = iframeRefRaw !== null && iframeRefRaw !== void 0 ? iframeRefRaw : iframeRef;
    var _h = __read(useState(true), 2), loading = _h[0], setLoading = _h[1];
    var retpath = useMemo(function () {
        return canUseDOM ? retpathRaw || location.href : retpathRaw;
    }, [retpathRaw]);
    var _j = __read(useState(preloadRaw), 2), preload = _j[0], setPreload = _j[1];
    var preloadParams = useRef({ colorScheme: colorScheme, theme: theme, retpath: retpath });
    var origin = useMemo(function () {
        if (host) {
            return 'https://' + host;
        }
        if (canUseDOM) {
            var params = new URL(location.href).searchParams;
            var hostParam = params.get('user_id_host');
            if (hostParam) {
                return 'https://' + hostParam;
            }
        }
        return 'https://yandex.' + tld;
    }, [host, tld]);
    var src = useMemo(function () {
        if (!preload &&
            (preloadParams.current.colorScheme !== colorScheme ||
                preloadParams.current.theme !== theme ||
                preloadParams.current.retpath !== retpath)) {
            preloadParams.current = { colorScheme: colorScheme, theme: theme, retpath: retpath };
        }
        var base = new URL('user-id', origin);
        preloadParams.current.retpath && base.searchParams.set('retpath', preloadParams.current.retpath);
        preloadParams.current.colorScheme && base.searchParams.set('colorScheme', preloadParams.current.colorScheme);
        preloadParams.current.theme && base.searchParams.set('theme', preloadParams.current.theme);
        lang && base.searchParams.set('l10n', lang);
        if (Array.isArray(features)) {
            var flag = features.join(',');
            base.searchParams.set('exp_flags', flag);
        }
        project && base.searchParams.set('project', project);
        env && base.searchParams.set('env', env);
        base.searchParams.set('dir', dir);
        for (var paramKey in queryParams) {
            base.searchParams.set(paramKey, queryParams[paramKey]);
        }
        return base.toString();
    }, [origin, features, project, queryParams, colorScheme, theme, retpath, preload]);
    useEffect(function () {
        if (!preload && (preloadRaw || fetchUserData)) {
            setPreload(true);
        }
    }, [preload, preloadRaw, setPreload, fetchUserData]);
    var platform = usePlatform({ platform: platformRaw, touchBreakpointWidth: touchBreakpointWidth });
    var onAvatarPointerEnter = useCallback(function () {
        if (!preload) {
            setPreload(true);
        }
    }, [preload, setPreload]);
    var size = useMemo(function () {
        if (typeof avatarSize === 'number') {
            return avatarSize;
        }
        if (platform === 'touch')
            return avatarSize[1];
        return avatarSize[0];
    }, [platform, avatarSize]);
    var onClose = useCallback(function () {
        var _a;
        (_a = avatarRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        onCloseRaw === null || onCloseRaw === void 0 ? void 0 : onCloseRaw();
    }, [onCloseRaw]);
    var _k = __read(useState(), 2), contentHeight = _k[0], setContentHeightState = _k[1];
    var _l = __read(useState(userData), 2), avatarDataProps = _l[0], setUserData = _l[1];
    useEffect(function () {
        setUserData(userData);
    }, [userData]);
    var setContentHeight = useCallback(function (height) {
        if (!canUseDOM)
            return;
        if (platform === 'desktop') {
            setContentHeightState(height);
            return;
        }
        var clientHeight = getClientHeight();
        setContentHeightState(clientHeight);
    }, [platform]);
    useIframeMaxHeight({ enabled: platform === 'touch', onHeightChange: setContentHeight });
    var rpcHandler = useCallback(function (rpcData, sendCallback) {
        var _a;
        switch (rpcData.methodCall) {
            case 'onInit':
                if (fetchUserData) {
                    var data = rpcData.params[0];
                    setUserData(data);
                }
                break;
            case 'onDataLoad':
            case 'updateUserHeight':
                var height = rpcData.params[0].height;
                setContentHeight(height);
                setLoading(false);
                break;
            case 'ITEM_CLICK':
                var item = rpcData.params[0].item;
                onMenuItemClick === null || onMenuItemClick === void 0 ? void 0 : onMenuItemClick(item);
                break;
            case 'closeUserID':
                onClose === null || onClose === void 0 ? void 0 : onClose();
                break;
            case 'updateTheme':
                var theme_1 = rpcData.params[0].theme;
                onThemeChange === null || onThemeChange === void 0 ? void 0 : onThemeChange(theme_1);
                break;
            default:
                var handler = rpcHandlers === null || rpcHandlers === void 0 ? void 0 : rpcHandlers[rpcData.methodCall];
                if (!handler)
                    return;
                Promise.resolve((_a = rpcHandlers === null || rpcHandlers === void 0 ? void 0 : rpcHandlers[rpcData.methodCall]) === null || _a === void 0 ? void 0 : _a.call.apply(_a, __spread([rpcHandlers], rpcData.params))).then(sendCallback);
                break;
        }
    }, [rpcHandlers, onMenuItemClick, onClose, onThemeChange, setContentHeight, setUserData, fetchUserData]);
    var sendMessage = useUserRPC({ loading: loading, iframeRef: iframeRef, origin: origin, rpcHandler: rpcHandler }).sendMessage;
    useEffect(function () {
        sendMessage({
            methodCall: 'updateRetpath',
            params: [{ retpath: retpath }],
        });
    }, [retpath, sendMessage]);
    useEffect(function () {
        sendMessage({
            methodCall: 'updateTheme',
            params: [{ colorScheme: colorScheme, theme: theme }],
        });
    }, [colorScheme, sendMessage, theme]);
    var sendFocusFirst = useCallback(function (fromKeyboard) {
        sendMessage({
            methodCall: 'focusFirst',
            params: [{ fromKeyboard: fromKeyboard }],
        });
    }, [sendMessage]);
    var onClick = useCallback(function () {
        if (visible) {
            sendFocusFirst(true);
            onClose();
        }
        else {
            if (!preload) {
                setPreload(true);
            }
            sendFocusFirst(false);
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
    }, [sendFocusFirst, onOpen, preload, visible, onClose]);
    useOverlay({
        visible: visible,
        onClose: onClose,
        essentialRefs: [dialogRef, avatarRef],
    });
    var contentProps = {
        colorScheme: colorScheme,
        height: contentHeight,
        loading: loading,
        preload: preload,
        ref: iframeRef,
        src: src,
        width: platform === 'desktop' ? 320 : '100%',
    };
    var dialogProps = {
        colorScheme: colorScheme,
        platform: platform,
        ref: dialogRef,
        visible: visible,
        onClose: onClose,
        loading: loading,
    };
    var avatarProps = {
        avatarSize: size,
        env: env,
        onClick: onClick,
        onPointerEnter: onAvatarPointerEnter,
        platform: platform,
        ref: avatarRef,
        retpath: retpath,
        userData: avatarDataProps,
        visible: visible,
    };
    return {
        avatarProps: avatarProps,
        contentProps: contentProps,
        dialogProps: dialogProps,
    };
}
